import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  HostListener,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router, RouterModule } from '@angular/router';
import { FirstContactModule } from '@app/first-contact/first-contact.module';
import { LibraryRouter } from '@app/models/library.router';
import { IMenuItem, UNCONTROLLED_ROUTES } from '@app/models/routes';
import { NotificationsService } from '@app/notifications/notifications.service';
import {
  forceLoadAccountCheck,
  loadAccountCheck,
  loadBillingSetting,
  loadProfileSettings,
} from '@app/store/actions/data.actions';
import { getLibraryRouteData } from '@app/store/reducers/library.reducer';
import { CurrencyNotifierComponent } from '@core/components/currency-notifier/currency-notifier.component';
import { CurrencySelectorModule } from '@core/components/currency-selector/currency-selector.module';
import { LanguageSelectorModule } from '@core/components/language-selector/language-selector.module';
import { MobileModalModule } from '@core/components/mobile-modal/mobile-modal.module';
import { ModalModule } from '@core/components/modal/modal.module';
import { StickyModule } from '@core/components/sticky/sticky.module';
import { SubjectSelectorModule } from '@core/components/subject-selector-header/subject-selector.module';
import { TimezoneNotifierComponent } from '@core/components/timezone-notifier/timezone-notifier.component';
import { TimezoneSelectorComponent } from '@core/components/timezone-selector/timezone-selector.component';
import { VacationInformerComponent } from '@core/components/vacation-informer/vacation-informer.component';
import { SuggestAddCardComponent } from '@core/header/suggest-add-card/suggest-add-card.component';
import { SuggestLanguageChangeComponent } from '@core/header/suggest-language-change/suggest-language-change.component';
import { SystemNotificationsComponent } from '@core/header/system-notifications/system-notifications.component';
import { UserMenuComponent } from '@core/header/user-menu/user-menu.component';
import { LeftSidebarComponent } from '@core/layouts/main/sidebar/left-sidebar.component';
import { ChatService } from '@core/services/chat.service';
import { NotVerifiedWidgetModule, VerificationWidgetModule } from '@core/widgets';
import { ChangableComponent } from '@models/changable.component';
import { Store } from '@ngrx/store';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ErrorNotificationService } from '@shared/error-notification/error-notification.service';
import { IconComponent } from '@shared/icon/icon.component';
import { setProfile } from '@store/actions/profile.actions';
import { getAccountCheck } from '@store/reducers/data.reducer';
import { getMe } from '@store/reducers/profile.reducer';
import {
  currencies,
  EContentPanelRoute,
  FeatureEnum,
  AccountCheck,
  TenantEnum,
  User,
  UserAchievementEnum,
  UserProfile,
  UserSegmentEnum,
  UserStatusEnum,
} from 'lingo2-models';
import { UserProfileVerificationEnum as VE } from 'lingo2-models/dist/account/enums/user-profile-verification-enum';
import { cloneDeep, uniq } from 'lodash';
import { ClickOutsideModule } from 'ng-click-outside';
import { DeviceDetectorService } from 'ngx-device-detector';
import { OnUiButtonModule, OnUiButtonState, OnUiPopoverModule, OnUiSelectModule } from 'onclass-ui';
import { filter, switchMap, takeUntil } from 'rxjs';
import { tap } from 'rxjs/operators';
import { SidebarService } from '../layouts/main/sidebar/sidebar.service';
import {
  AccountService,
  AuthModalModeType,
  AuthService,
  ContextService,
  FeaturesService,
  AnalyticsService,
  PlatformService,
  ProfileService,
  ScreenService,
  AuthModalOptions,
  CrispChatService,
} from '../services';
import { EventActionEnum, EventCategoryEnum } from '../services/analytics';

enum StatusActions {
  send = 'send',
  cancel = 'cancel',
}

const menuItems: IMenuItem[] = [
  {
    id: 'main',
    label: 'menu.top.main',
    icon: 'header-main',
    route: EContentPanelRoute.main,
  },
  {
    id: 'classes',
    label: 'menu.top.classes',
    icon: 'header-classes',
    route: EContentPanelRoute.classes,
  },
  {
    id: 'teachers',
    label: 'menu.top.teachers',
    icon: 'header-teachers',
    route: EContentPanelRoute.tutors,
  },
  {
    id: 'library',
    label: 'menu.top.library',
    icon: 'header-lessons',
    route: EContentPanelRoute.lessons,
  },
  {
    id: 'collections',
    icon: 'header-collections',
    label: 'library.base-pages.collections',
    route: EContentPanelRoute.collections,
  },
  {
    id: 'schools',
    icon: 'header-schools',
    label: 'menu.left-sidebar.schools',
    route: EContentPanelRoute.schools,
  },
];

@Component({
  selector: 'app-header',
  standalone: true,
  imports: [
    CommonModule,
    TranslateModule,
    LanguageSelectorModule,
    CurrencySelectorModule,
    RouterModule,
    ClickOutsideModule,
    SubjectSelectorModule,
    MobileModalModule,
    FormsModule,
    IconComponent,
    VerificationWidgetModule,
    OnUiButtonModule,
    OnUiPopoverModule,
    NotVerifiedWidgetModule,
    ModalModule,
    FirstContactModule,
    OnUiSelectModule,
    UserMenuComponent,
    SystemNotificationsComponent,
    SuggestLanguageChangeComponent,
    SuggestAddCardComponent,
    TimezoneNotifierComponent,
    StickyModule,
    VacationInformerComponent,
    CurrencyNotifierComponent,
    LeftSidebarComponent,
    TimezoneSelectorComponent,
  ],
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HeaderComponent extends ChangableComponent implements OnInit, AfterViewInit, OnDestroy {
  @HostListener('window:message', ['$event'])
  public onMessage(event: MessageEvent) {
    this.receiveMessage(event);
  }
  public isSmallScreen = this.screenService.isSmallScreen;
  public isWideScreen = this.screenService.isWideScreen;
  public me: User;
  public isGuest: boolean;
  public profile: UserProfile;
  public userAccountCheck: Partial<AccountCheck>;
  public options_open = false;
  public showMoreMenu = false;
  public isOpenedPopover = true;
  public accountRoute = AccountService.accountRoute;
  public menuItems = cloneDeep(menuItems);
  public menus = [];
  public unreadNotificationsCount$ = this.notificationsService.unreadCount$;
  public expandUserMenuState = false;
  public checkedSteps: number;
  public scrollThreshold = 0;
  public maxSteps: number;
  private libraryRouter: LibraryRouter = new LibraryRouter();
  public verification_status: VE;
  public isLoading = false;
  public btnState: OnUiButtonState = 'default';
  public unreadCount: number;

  public openIntroductionModal: boolean;
  public term = '';
  public showMenuWrap: boolean;
  public baseRoute: EContentPanelRoute;

  public focus: boolean;
  public contentDisplay: boolean;
  public recentSearchesList: string[] = [];

  public constructor(
    public errorNotificationService: ErrorNotificationService,
    private authService: AuthService,
    private contextService: ContextService,
    private profileService: ProfileService,
    private screenService: ScreenService,
    private sidebarService: SidebarService,
    private features: FeaturesService,
    private analytics: AnalyticsService,
    private crispChat: CrispChatService,
    private chatService: ChatService,
    private notificationsService: NotificationsService,
    private translate: TranslateService,
    private readonly store: Store,
    private router: Router,
    public deviceService: DeviceDetectorService,
    protected cdr: ChangeDetectorRef,
    protected platform: PlatformService,
  ) {
    super(cdr, platform);
  }

  public ngOnInit(): void {
    this.store
      .select(getMe)
      .pipe(
        filter((me) => !!me),
        tap((me: User) => {
          this.me = me;
          this.isGuest = me?.status === UserStatusEnum.guest;
          this.store.dispatch(loadAccountCheck());
          this.store.dispatch(loadProfileSettings());
          this.store.dispatch(loadBillingSetting({ force: true }));
          this.detectChanges();
        }),
        switchMap((me) => this.profileService.getProfile(me.id)),
        takeUntil(this.destroyed$),
      )
      .subscribe({
        next: (profile) => {
          this.profile = profile;
          this.contextService.patchProfile(this.profile);
          this.maxSteps = AccountService.isAsIfTeacher(this.me) ? 6 : 2;
          this.setAllSteps();
          this.updateRoutes();
          this.detectChanges();
        },
        error: (error) => {
          this.errorNotificationService.captureError(error, 'LOAD-SOMEDATA');
        },
      });

    this.contextService.profile$.pipe(takeUntil(this.destroyed$)).subscribe((res) => {
      this.verification_status = res?.verification_status;
      this.detectChanges();
    });

    this.store
      .select(getAccountCheck)
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: (accountCheck) => {
          this.userAccountCheck = accountCheck;
          this.detectChanges();
        },
        error: (error) => {
          this.errorNotificationService.captureError(error, 'OTHER-PROBLEM');
        },
      });
  }

  public ngAfterViewInit() {
    this.screenService.width$.pipe(takeUntil(this.destroyed$)).subscribe({
      next: () => {
        this.isSmallScreen = this.screenService.isSmallScreen;
        this.isWideScreen = this.screenService.isWideScreen;
        this.hideMenuItems();
      },
      error: (error) => {
        this.errorNotificationService.captureError(error, 'OTHER-PROBLEM');
        this.isSmallScreen = this.screenService.isSmallScreen;
        this.isWideScreen = this.screenService.isWideScreen;
        this.hideMenuItems();
      },
    });

    this.store
      .select(getLibraryRouteData)
      .pipe(
        filter((d) => !!d),
        takeUntil(this.destroyed$),
      )
      .subscribe({
        next: (data) => {
          this.baseRoute = data.baseRoute;
          this.libraryRouter.setBaseRoute(data.baseRoute);
          this.libraryRouter.setSubjects(data.subjectCodes);
          this.libraryRouter.setCurrency(data.currency);
          this.term = data.term;
          if (this.isBrowser) {
            this.recentSearchesList = JSON.parse(localStorage.getItem('recentSearches')) || [];
          }
          this.updateRoutes();
          this.hideMenuItems();
        },
        error: (error) => {
          this.errorNotificationService.captureError(error, 'LOAD-SOMEDATA');
          this.libraryRouter.setBaseRoute(this.libraryRouter.getBaseRoute());
          this.libraryRouter.setSubjects(this.libraryRouter.getSubjects());
          this.updateRoutes();
          this.hideMenuItems();
        },
      });
  }

  public isMeVersion() {
    return this.features.tenant === TenantEnum.onclass_me;
  }

  public showMainMenu() {
    return !this.isProfilePage && !this.isConstructorPage;
  }

  public get isAvailableProfileFinance() {
    return this.features.isAvailable(FeatureEnum.profile_finance, this.me);
  }

  public get isProfilePage(): boolean {
    return this.router.url.includes('/profile');
  }

  public get isConstructorPage(): boolean {
    return this.router.url.includes('/constructor') || this.router.url.includes('/content/collections');
  }

  public get isTeacher(): boolean {
    return AccountService.isAsIfTeacher(this.me);
  }

  public get isOnModify() {
    if (this.verification_status === VE.changed) {
      return StatusActions.send;
    } else if (this.verification_status === VE.resent || this.verification_status === VE.sent) {
      return StatusActions.cancel;
    }
    return;
  }

  public get isCommonLanding(): boolean {
    return this.router.url === '/';
  }

  public get isNeedAction(): string {
    if (this.isMeVersion()) {
      return '';
    }
    return !this.isGuest && this.isTeacher ? this.getStatusIcon(this.verification_status) : '';
  }

  public getStatusIcon(status: VE): string {
    switch (status) {
      case VE.incomplete:
      case VE.not_verified:
      case VE.changed:
      case VE.declined:
      case VE.redeclined:
        return 'status-need-action';
      case VE.sent:
      case VE.resent:
        return 'status-on-moderation-no-border';
      default:
        return '';
    }
  }

  public get percent(): number {
    if (!this.maxSteps || !this.checkedSteps) {
      return 0;
    }
    return Math.round((100 / this.maxSteps) * this.checkedSteps);
  }

  private setAllSteps(): boolean {
    const segments = this.isTeacher
      ? [UserSegmentEnum.has_approved_bank_card, UserSegmentEnum.use_mobile_app]
      : [UserSegmentEnum.use_mobile_app];

    const achievements = this.isTeacher
      ? [UserAchievementEnum.added_working_time_once, UserAchievementEnum.created_user_services_once]
      : [];

    const filteredSegments = this.me?.segments
      .filter((_a) => segments.includes(_a.segment))
      .sort((a, b) => {
        const _a = new Date(a.created_at);
        const _b = new Date(b.created_at);
        return _b > _a ? 1 : -1;
      });

    const filteredAchievements = this.me?.achievements
      .filter((_a) => achievements.includes(_a.achievement))
      .sort((a, b) => {
        const _a = new Date(a.created_at);
        const _b = new Date(b.created_at);
        return _b > _a ? 1 : -1;
      });

    const isTeacherAllStepsFilled = (
      this.isTeacher
        ? AccountService.isAllStepsFilledProfileTeacher(this.userAccountCheck)
        : AccountService.isAllStepsFilledProfileStudent(this.userAccountCheck)
    )
      ? 1
      : 0;

    const teachingSubjects = this.profile?.teaching_subjects?.length > 0 ? 1 : 0;

    this.checkedSteps =
      filteredSegments.length + filteredAchievements.length + teachingSubjects + isTeacherAllStepsFilled;

    return this.checkedSteps === this.maxSteps;
  }

  public get isAllStepsFilled() {
    return this.checkedSteps === this.maxSteps;
  }

  public authorize(mode: AuthModalModeType) {
    this.authService.showAuthModal(() => {}, {
      caller: 'app-header',
      reason: mode === 'social' ? 'sign in' : 'sign up',
      view: mode,
    } as AuthModalOptions);
  }

  public isMenuItemEnabled(item: IMenuItem): boolean {
    // '/feed' доступен только после явной или неявной авторизации
    return item.id === 'feed' ? !!this.me : true;
  }

  public toggleLeftSidebar() {
    this.sidebarService.toggleLeftSidebar();
  }

  public get isAvailableNotifications(): boolean {
    return this.features.isAvailable(FeatureEnum.notifications, this.me);
  }

  public expandedUserMenuState(state: boolean) {
    this.expandUserMenuState = state;
    this.detectChanges();
  }

  private updateRoutes(menuRoutes = menuItems) {
    this.menuItems = this.setBaseRoutes(menuRoutes);
  }

  public goToMain(): void {
    this.libraryRouter.setBaseRoute(EContentPanelRoute.main);
    this.libraryRouter.setCurrency(this.getMyCurrency());
    this.libraryRouter.setSubject(sessionStorage.getItem('selectedSubject'));
    this.libraryRouter.setTerm(this.term);
    void this.router.navigate([this.libraryRouter.getLink().path]).then();
  }

  private setBaseRoutes(menuRoutes = menuItems) {
    return menuRoutes.map((item) => {
      this.libraryRouter.setSubject(sessionStorage.getItem('selectedSubject'));
      this.libraryRouter.setBaseRoute(item.route);
      this.libraryRouter.setCurrency(this.getMyCurrency());
      let url = `/${item.route}`;
      if (!UNCONTROLLED_ROUTES.includes(item.route)) {
        url = this.libraryRouter.getLink().path;
      }
      return {
        ...item,
        url,
      };
    });
  }

  public getQueryParams() {
    return this.libraryRouter.getLink().parameters;
  }

  isItemActive(item: IMenuItem): boolean {
    const currentUrl = this.router.url;
    const itemUrlTree = this.router.createUrlTree([item.url]);
    return this.router.serializeUrl(itemUrlTree) === currentUrl;
  }

  /**
   * hide or show menu items
   */
  private hideMenuItems(): void {
    if (this.isServer) {
      return;
    }

    if (window.innerWidth > 1200 || window.innerWidth <= 870) {
      this.menuItems = cloneDeep(menuItems);
      this.updateRoutes(this.menuItems);
      this.menus = [];
      this.showMoreMenu = false;
      return;
    }
    this.showMoreMenu = this.menus.length > 0;
    this.detectChanges();
  }

  public sendToVerification(): void {
    const oldStatus = this.verification_status;
    this.btnState = 'progress';

    this.isLoading = true;
    this.profileService
      .sendToVerification()
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: (res) => {
          if (res.status === 'ok') {
            if (oldStatus === VE.not_verified) {
              // отправился первый раз
              this.analytics.event(EventActionEnum.verification_sent, EventCategoryEnum.engagement, 'app-header');
            } else {
              this.analytics.event(EventActionEnum.verification_resent, EventCategoryEnum.engagement, 'app-header');
            }
            this.updateProfile();
          }

          this.btnState = 'done';
          this.setTimeout(() => {
            this.btnState = 'default';
            this.detectChanges();
          }, 1000);
          this.isLoading = false;
          this.detectChanges();
        },
        error: (error) => {
          this.errorNotificationService.captureError(error, 'PROFILE-PROBLEM');
          this.btnState = 'default';
          this.isLoading = false;
          this.detectChanges();
        },
      });
  }

  public cancelVerification(): void {
    this.isLoading = true;
    this.btnState = 'progress';
    this.profileService
      .cancelVerification()
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: (res) => {
          if (res.status === 'ok') {
            this.analytics.event(EventActionEnum.verification_canceled, EventCategoryEnum.engagement, 'app-header');
            this.updateProfile();
          }
          this.btnState = 'done';
          this.setTimeout(() => {
            this.btnState = 'default';
            this.detectChanges();
          }, 1000);
          this.isLoading = false;
          this.detectChanges();
        },
        error: (error) => {
          this.errorNotificationService.captureError(error, 'PROFILE-PROBLEM');
          this.btnState = 'default';
          this.isLoading = false;
          this.detectChanges();
        },
      });
  }

  private updateProfile(): void {
    this.profileService
      .getProfile(this.me?.id)
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: (profile) => {
          this.verification_status = profile.verification_status;
          this.contextService.updateProfile(profile);
          this.store.dispatch(setProfile({ profile }));
          this.store.dispatch(forceLoadAccountCheck());
          this.detectChanges();
        },
        error: (error) => {
          this.errorNotificationService.captureError(error, 'PROFILE-PROBLEM');
        },
      });
  }

  public getMyCurrency() {
    if (this.me?.currency_id) {
      return this.translate.instant(currencies[this.me?.currency_id]?.title);
    } else {
      return this.translate.instant('USD');
    }
  }

  public trackByFn(index) {
    return index;
  }

  public onFocus() {
    this.focus = true;
    this.contentDisplay = true;
  }

  public onBlur() {
    this.focus = false;
  }

  public onCloseContent() {
    this.contentDisplay = false;
    this.detectChanges();
  }

  public deleteSearch(term: string) {
    this.recentSearchesList = this.recentSearchesList.filter((el) => el !== term);
    if (this.isBrowser) {
      localStorage.setItem('recentSearches', JSON.stringify(this.recentSearchesList));
    }
  }

  public onOptionsClick(event: MouseEvent) {
    event.stopPropagation();
    event.stopImmediatePropagation();
  }

  public deleteSearchHistory() {
    this.recentSearchesList = [];
    if (this.isBrowser) {
      localStorage.setItem('recentSearches', JSON.stringify([]));
    }
  }

  public onTermChange(searchInput?: HTMLElement) {
    const term = this.term.trim();
    this.libraryRouter.setBaseRoute(this.baseRoute);
    this.libraryRouter.setTerm(term);
    if (!!term.length) {
      if (this.isBrowser) {
        const allSearches = uniq([term, ...this.recentSearchesList]).slice(0, 5);
        localStorage.setItem('recentSearches', JSON.stringify(allSearches));
        searchInput?.blur();
        this.onCloseContent();
      }
    }
    const link = this.libraryRouter.getLink();

    if (this.isContentPage()) {
      void this.router.navigate([link.path], { queryParams: link.parameters }).then();
    } else {
      const subjects = (this.libraryRouter.getSubjects() || []).filter((s) => !!s);
      if (subjects.length) {
        void this.router.navigate([subjects[0], EContentPanelRoute.main], { queryParams: link.parameters }).then();
      } else {
        void this.router.navigate([EContentPanelRoute.main], { queryParams: link.parameters }).then();
      }
    }
    this.detectChanges();
  }

  public onTermSet(term: string) {
    this.term = term;
    this.onTermChange();
  }

  public clearSearchForm() {
    this.term = '';
    this.onTermChange();
  }

  public isContentPage() {
    return [
      EContentPanelRoute.lessons,
      EContentPanelRoute.tutors,
      EContentPanelRoute.classes,
      EContentPanelRoute.schools,
      EContentPanelRoute.collections,
    ].includes(this.baseRoute);
  }

  public openCrispChat() {
    this.crispChat.openChat();
    this.isOpenedPopover = false;
  }

  public onToggleIntroductionModal() {
    this.openIntroductionModal = !this.openIntroductionModal;
    this.isOpenedPopover = false;
    this.detectChanges();
  }

  public toggleChat() {
    this.chatService.openChat();
    this.detectChanges();
  }

  public receiveMessage(event: MessageEvent) {
    const message = event?.data;
    if (typeof message === 'string' || message instanceof String) {
      if (message.includes('chatapp')) {
        const messageArray = message.split('::');
        const action = messageArray[1];
        const value = messageArray[2];
        if (action && action === 'windowstatus') {
        } else if (action && action === 'unreadcount') {
          // Кол-во непрочитанных сообщений
          this.unreadCount = Number(value);
        }
      }
    }
  }
}
